import { Dictionary } from 'lodash';
import { ByDimension, Dimension } from '../../types';
import { ByLanguage } from '../types';
import { EQUAL_WEIGHTS_TITLE } from '../value-sets';
import { ByImpactCategory, UIImpactCategoryByCode } from './types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';

export const dimensionNamesByLanguage: ByLanguage<ByDimension<string>> = {
  en: {
    [Dimension.SOCIAL]: 'Society',
    [Dimension.KNOWLEDGE]: 'Knowledge',
    [Dimension.HEALTH]: 'Health',
    [Dimension.ENVIRONMENT]: 'Environment',
  },
  fi: {
    [Dimension.SOCIAL]: 'Yhteiskunta',
    [Dimension.KNOWLEDGE]: 'Tieto',
    [Dimension.HEALTH]: 'Terveys',
    [Dimension.ENVIRONMENT]: 'Ympäristö',
  },
  sv: {
    [Dimension.SOCIAL]: 'Samhälle',
    [Dimension.KNOWLEDGE]: 'Kunskap',
    [Dimension.HEALTH]: 'Hälsa',
    [Dimension.ENVIRONMENT]: 'Miljö',
  },
};

export const impactNamesByLanguage: ByLanguage<ByImpactCategory<string>> = {
  en: {
    [ImpactCategoryId.JOBS]: 'Jobs',
    [ImpactCategoryId.TAXES]: 'Taxes',
    [ImpactCategoryId.SOCIETAL_INFRA]: 'Societal infrastructure',
    [ImpactCategoryId.SOCIETAL_STABILITY]: 'Societal stability & understanding among people', // See also friendlyShortImpactTitle
    [ImpactCategoryId.EQUALITY]: 'Equality & human rights',
    [ImpactCategoryId.KNOWLEDGE_INFRA]: 'Knowledge infrastructure',
    [ImpactCategoryId.CREATES_KNOWLEDGE]: 'Creating knowledge',
    [ImpactCategoryId.DISTRIBUTING_KNOWLEDGE]: 'Distributing knowledge',
    [ImpactCategoryId.SCARCE_HUMAN_CAPITAL]: 'Scarce human capital',
    [ImpactCategoryId.PHYSICAL_DISEASES]: 'Physical diseases',
    [ImpactCategoryId.MENTAL_DISEASES]: 'Mental diseases',
    [ImpactCategoryId.NUTRITION]: 'Nutrition',
    [ImpactCategoryId.RELATIONSHIPS]: 'Relationships',
    [ImpactCategoryId.MEANING_AND_JOY]: 'Meaning & joy',
    [ImpactCategoryId.GHG_EMISSIONS]: 'GHG emissions',
    [ImpactCategoryId.NON_GHG_EMISSIONS]: 'Non-GHG emissions',
    [ImpactCategoryId.SCARCE_NATURAL_RESOURCES]: 'Scarce natural resources',
    [ImpactCategoryId.BIODIVERSITY]: 'Biodiversity',
    [ImpactCategoryId.WASTE]: 'Waste',
  },
  fi: {
    [ImpactCategoryId.JOBS]: 'Työpaikat',
    [ImpactCategoryId.TAXES]: 'Verot',
    [ImpactCategoryId.SOCIETAL_INFRA]: 'Yhteiskunnan infrastruktuuri',
    [ImpactCategoryId.SOCIETAL_STABILITY]: 'Yhteiskunnan vakaus & jaettu ymmärrys', // See also friendlyShortImpactTitle
    [ImpactCategoryId.EQUALITY]: 'Tasa-arvo ja ihmisoikeudet',
    [ImpactCategoryId.KNOWLEDGE_INFRA]: 'Tietoinfrastruktuuri',
    [ImpactCategoryId.CREATES_KNOWLEDGE]: 'Tiedon luominen',
    [ImpactCategoryId.DISTRIBUTING_KNOWLEDGE]: 'Tiedon jakaminen',
    [ImpactCategoryId.SCARCE_HUMAN_CAPITAL]: 'Niukka osaamispääoma',
    [ImpactCategoryId.PHYSICAL_DISEASES]: 'Sairaudet',
    [ImpactCategoryId.MENTAL_DISEASES]: 'Mielenterveys',
    [ImpactCategoryId.NUTRITION]: 'Ravinto',
    [ImpactCategoryId.RELATIONSHIPS]: 'Ihmissuhteet',
    [ImpactCategoryId.MEANING_AND_JOY]: 'Merkitys & ilo',
    [ImpactCategoryId.GHG_EMISSIONS]: 'Kasvihuonekaasupäästöt',
    [ImpactCategoryId.NON_GHG_EMISSIONS]: 'Muut päästöt',
    [ImpactCategoryId.SCARCE_NATURAL_RESOURCES]: 'Niukat luonnonvarat',
    [ImpactCategoryId.BIODIVERSITY]: 'Biodiversiteetti',
    [ImpactCategoryId.WASTE]: 'Jätteet',
  },
  sv: {
    [ImpactCategoryId.JOBS]: 'Jobb',
    [ImpactCategoryId.TAXES]: 'Skatter',
    [ImpactCategoryId.SOCIETAL_INFRA]: 'Samhällsinfrastruktur',
    [ImpactCategoryId.SOCIETAL_STABILITY]: 'Samhällsstabilitet',
    [ImpactCategoryId.EQUALITY]: 'Jämställdhet och mänskliga rättigheter', // See also friendlyShortImpactTitle
    [ImpactCategoryId.KNOWLEDGE_INFRA]: 'Kunskapens infrastruktur',
    [ImpactCategoryId.CREATES_KNOWLEDGE]: 'Skapande av kunskap',
    [ImpactCategoryId.DISTRIBUTING_KNOWLEDGE]: 'Spriding av kunskap',
    [ImpactCategoryId.SCARCE_HUMAN_CAPITAL]: 'Knappt humankapital',
    [ImpactCategoryId.PHYSICAL_DISEASES]: 'Fysiska sjukdomar',
    [ImpactCategoryId.MENTAL_DISEASES]: 'Psykiska sjukdomar',
    [ImpactCategoryId.NUTRITION]: 'Näring',
    [ImpactCategoryId.RELATIONSHIPS]: 'Relationer',
    [ImpactCategoryId.MEANING_AND_JOY]: 'Mening och lycka',
    [ImpactCategoryId.GHG_EMISSIONS]: 'Utsläpp av växthusgaser',
    [ImpactCategoryId.NON_GHG_EMISSIONS]: 'Övriga utsläpp',
    [ImpactCategoryId.SCARCE_NATURAL_RESOURCES]: 'Knappa naturresurser',
    [ImpactCategoryId.BIODIVERSITY]: 'Biologisk mångfald',
    [ImpactCategoryId.WASTE]: 'Avfall',
  },
};

export enum ImpactProfileTitle {
  IMPACT = 'IMPACT',
  NEGATIVE = 'NEGATIVE',
  SCORE = 'SCORE',
  POSITIVE = 'POSITIVE',
}

export const titleByLanguage: ByLanguage<{
  [title in ImpactProfileTitle]: string;
}> = {
  en: {
    [ImpactProfileTitle.IMPACT]: 'Impact',
    [ImpactProfileTitle.NEGATIVE]: 'Negative',
    [ImpactProfileTitle.SCORE]: 'Score',
    [ImpactProfileTitle.POSITIVE]: 'Positive',
  },
  fi: {
    [ImpactProfileTitle.IMPACT]: 'Vaikutus',
    [ImpactProfileTitle.NEGATIVE]: 'Negatiivinen',
    [ImpactProfileTitle.SCORE]: 'Pisteet',
    [ImpactProfileTitle.POSITIVE]: 'Positiivinen',
  },
  sv: {
    [ImpactProfileTitle.IMPACT]: 'Påverkan',
    [ImpactProfileTitle.NEGATIVE]: 'Negativ',
    [ImpactProfileTitle.SCORE]: 'Poängtal',
    [ImpactProfileTitle.POSITIVE]: 'Positiv',
  },
};

export const valueSetByLanguage: ByLanguage<Dictionary<string>> = {
  en: {
    [EQUAL_WEIGHTS_TITLE]: EQUAL_WEIGHTS_TITLE,
    // TODO translate other value sets when needed
  },
  fi: {
    [EQUAL_WEIGHTS_TITLE]: 'Tasapainotettu',
  },
  sv: {
    [EQUAL_WEIGHTS_TITLE]: 'Lika vikt.', // ...ning
  },
};

export const impactVerbsByCategory = {
  [ImpactCategoryId.JOBS]: {
    positiveVerb: 'creating',
    positiveName: 'jobs',
    negativeVerb: '',
    negativeName: '',
  },
  [ImpactCategoryId.TAXES]: {
    positiveVerb: 'generating',
    positiveName: 'taxes',
    negativeVerb: '',
    negativeName: '',
  },
  [ImpactCategoryId.SOCIETAL_INFRA]: {
    positiveVerb: 'creating',
    positiveName: 'societal infrastructure',
    negativeVerb: '',
    negativeName: '',
  },
  [ImpactCategoryId.SOCIETAL_STABILITY]: {
    positiveVerb: 'increasing',
    positiveName: 'societal stability & understanding among people',
    negativeVerb: 'decreasing',
    negativeName: 'societal stability & understanding among people',
  },
  [ImpactCategoryId.EQUALITY]: {
    positiveVerb: 'increasing',
    positiveName: 'equality & human rights',
    negativeVerb: 'eroding',
    negativeName: 'equality & human rights',
  },
  [ImpactCategoryId.KNOWLEDGE_INFRA]: {
    positiveVerb: 'creating',
    positiveName: 'knowledge infrastructure',
    negativeVerb: '',
    negativeName: '',
  },
  [ImpactCategoryId.CREATES_KNOWLEDGE]: {
    positiveVerb: 'creating',
    positiveName: 'knowledge',
    negativeVerb: '',
    negativeName: '',
  },
  [ImpactCategoryId.DISTRIBUTING_KNOWLEDGE]: {
    positiveVerb: 'enhancing',
    positiveName: 'the distribution of knowledge',
    negativeVerb: 'hindering',
    negativeName: 'the distribution of knowledge',
  },
  [ImpactCategoryId.SCARCE_HUMAN_CAPITAL]: {
    positiveVerb: '',
    positiveName: '',
    negativeVerb: 'using',
    negativeName: 'scarce human capital',
  },
  [ImpactCategoryId.PHYSICAL_DISEASES]: {
    positiveVerb: 'reducing',
    positiveName: 'physical diseases',
    negativeVerb: 'causing',
    negativeName: 'physical diseases',
  },
  [ImpactCategoryId.MENTAL_DISEASES]: {
    positiveVerb: 'reducing',
    positiveName: 'mental diseases',
    negativeVerb: 'causing',
    negativeName: 'mental diseases',
  },
  [ImpactCategoryId.NUTRITION]: {
    positiveVerb: 'enhancing',
    positiveName: 'nutrition',
    negativeVerb: 'causing',
    negativeName: 'impoverished nutrition',
  },
  [ImpactCategoryId.RELATIONSHIPS]: {
    positiveVerb: 'improving',
    positiveName: 'relationships',
    negativeVerb: 'deteriorating',
    negativeName: 'relationships',
  },
  [ImpactCategoryId.MEANING_AND_JOY]: {
    positiveVerb: 'creating',
    positiveName: 'meaning & joy among people',
    negativeVerb: 'reducing',
    negativeName: 'meaning & joy among people',
  },
  [ImpactCategoryId.GHG_EMISSIONS]: {
    positiveVerb: 'reducing',
    positiveName: 'GHG emissions',
    negativeVerb: 'causing',
    negativeName: 'GHG emissions',
  },
  [ImpactCategoryId.NON_GHG_EMISSIONS]: {
    positiveVerb: 'reducing',
    positiveName: 'non-GHG emissions',
    negativeVerb: 'causing',
    negativeName: 'non-GHG emissions',
  },
  [ImpactCategoryId.SCARCE_NATURAL_RESOURCES]: {
    positiveVerb: 'saving',
    positiveName: 'scarce natural resources',
    negativeVerb: 'using',
    negativeName: 'scarce natural resources',
  },
  [ImpactCategoryId.BIODIVERSITY]: {
    positiveVerb: 'increasing',
    positiveName: 'biodiversity',
    negativeVerb: 'reducing',
    negativeName: 'biodiversity',
  },
  [ImpactCategoryId.WASTE]: {
    positiveVerb: 'reducing',
    positiveName: 'waste',
    negativeVerb: 'generating',
    negativeName: 'waste',
  },
};

export const uiImpactCategoryByCode: UIImpactCategoryByCode = {
  [ImpactCategoryId.JOBS]: {
    code: ImpactCategoryId.JOBS,
    title: impactNamesByLanguage.en[ImpactCategoryId.JOBS],
    dimension: Dimension.SOCIAL,
    explanations: {
      impactCategoryCode: ImpactCategoryId.JOBS,
      topicsConsidered: [
        'Employing people either directly or indirectly and thereby enabling individuals to achieve financial autonomy and a sense of belonging and identity within society.',
      ],
      explanationsAndExamplesByValence: {
        positiveExplanation: `Employing people and thus enabling them to gain financial actorship and identity in society. In this impact, we take into account people employed both directly by the company, as well as indirectly by its suppliers and customers.`,
        positiveExample: 'Example: a company employs 700 people.',
      },
      productViewExplanation: `Employing people and thus giving them the opportunity to gain
         financial independence and a sense of belonging and identity in society.`,
    },
  },
  [ImpactCategoryId.TAXES]: {
    code: ImpactCategoryId.TAXES,
    title: impactNamesByLanguage.en[ImpactCategoryId.TAXES],
    dimension: Dimension.SOCIAL,
    explanations: {
      impactCategoryCode: ImpactCategoryId.TAXES,
      topicsConsidered: [
        'Corporate income taxes',
        'Value added taxes (VAT)',
        'Personal income taxes related to salaries',
        'Other taxes on goods and services',
      ],
      explanationsAndExamplesByValence: {
        positiveExplanation: `Contributing to joint resources via taxes paid directly by the company or indirectly by its suppliers and customers.`,
        positiveExample: 'Example: a company pays its corporate taxes and value-added taxes.',
      },
      productViewExplanation: 'Contributing to financing joint resources via paying taxes to government.',
    },
  },
  [ImpactCategoryId.SOCIETAL_INFRA]: {
    code: ImpactCategoryId.SOCIETAL_INFRA,
    title: impactNamesByLanguage.en[ImpactCategoryId.SOCIETAL_INFRA],
    dimension: Dimension.SOCIAL,
    explanations: {
      impactCategoryCode: ImpactCategoryId.SOCIETAL_INFRA,
      topicsConsidered: [
        'Built environment',
        'Energy',
        'Transportation',
        'Physical security',
        'Social care',
        'Finance',
        'Water and sanitation',
        'Industrial infrastructure',
      ],
      explanationsAndExamplesByValence: {
        positiveExplanation: `Contributing to or forming basic societal infrastructure, such as roads, sewage systems, electricity network, hospitals, schools, and pension systems. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company builds sewer systems.',
      },
      productViewExplanation: `Contributing to or forming basic societal infrastructure,
         such as roads, bridges, electricity networks, hospitals, schools and sewage systems.`,
    },
  },
  [ImpactCategoryId.SOCIETAL_STABILITY]: {
    code: ImpactCategoryId.SOCIETAL_STABILITY,
    title: impactNamesByLanguage.en[ImpactCategoryId.SOCIETAL_STABILITY],
    dimension: Dimension.SOCIAL,
    explanations: {
      impactCategoryCode: ImpactCategoryId.SOCIETAL_STABILITY,
      topicsConsidered: [
        'Social inclusion, cohesion and polarization',
        'Political (in)stability and corruption',
        'Interpersonal understanding and cultural consciousness',
        'Societal discussions, freedom of media and evidence-based worldview',
        'Violence, conflict, war and peace',
        'Security and privacy',
        'Organized crime and illicit trade',
        'Socioeconomic systemic risks and financial instability',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Decreasing understanding among people or inciting or enabling armed conflict. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example: a company produces firearms that are used in armed conflicts.`,
        positiveExplanation: `Increasing understanding among people or inciting or enabling peace. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: `Example: a company offers translation services that help people with no common language understand each other.`,
      },
      productViewExplanation:
        'Increasing or decreasing understanding among people or inciting or enabling armed conflicts.',
    },
  },
  [ImpactCategoryId.EQUALITY]: {
    code: ImpactCategoryId.EQUALITY,
    title: impactNamesByLanguage.en[ImpactCategoryId.EQUALITY],
    dimension: Dimension.SOCIAL,
    explanations: {
      impactCategoryCode: ImpactCategoryId.EQUALITY,
      topicsConsidered: [
        'Modern slavery and inhumane working conditions',
        'Exploitation of Indigenous Peoples',
        'Unequal access to basic services',
        'Racial, economic and gender equality and discriminatory practices',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Decreasing racial, economic or gender equality, or violating human rights. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example: a company uses conflict minerals to produce consumer electronics.`,
        positiveExplanation: `Increasing racial, economic or gender equality, or enforcing human rights. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: `Example: a company provides microloans to women in
         developing countries, enabling them to start a business.`,
      },
      productViewExplanation: `Enforcing or harming racial, economic or gender equality or human rights.`,
    },
  },
  [ImpactCategoryId.KNOWLEDGE_INFRA]: {
    code: ImpactCategoryId.KNOWLEDGE_INFRA,
    title: impactNamesByLanguage.en[ImpactCategoryId.KNOWLEDGE_INFRA],
    dimension: Dimension.KNOWLEDGE,
    explanations: {
      impactCategoryCode: ImpactCategoryId.KNOWLEDGE_INFRA,
      topicsConsidered: [
        'Enabling creation of data, information and knowledge',
        'Enabling distribution of data, information and knowledge',
        'Enabling management of data, information and knowledge',
        'Enabling utilization of data, information and knowledge',
      ],
      explanationsAndExamplesByValence: {
        positiveExplanation: `Contributing to knowledge infrastructure and thus enabling the effective and safe creation, distribution, and maintenance of knowledge, information, and data. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company produces base stations.',
      },
      productViewExplanation: `Contributing to knowledge infrastructure and thus enabling
         creation and distribution of knowledge, data and understanding.`,
    },
  },
  [ImpactCategoryId.CREATES_KNOWLEDGE]: {
    code: ImpactCategoryId.CREATES_KNOWLEDGE,
    title: impactNamesByLanguage.en[ImpactCategoryId.CREATES_KNOWLEDGE],
    dimension: Dimension.KNOWLEDGE,
    explanations: {
      impactCategoryCode: ImpactCategoryId.CREATES_KNOWLEDGE,
      topicsConsidered: [
        'Creation of informational content',
        'Processing of data to information and insights',
        'Collection of data',
        'Creation of art',
        'Innovations',
      ],
      explanationsAndExamplesByValence: {
        positiveExplanation: `Enabling, encouraging or practicing the creation of data, information or knowledge. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company providing preclinical research services.',
      },
      productViewExplanation: 'Creating knowledge and understanding.',
    },
  },
  [ImpactCategoryId.DISTRIBUTING_KNOWLEDGE]: {
    code: ImpactCategoryId.DISTRIBUTING_KNOWLEDGE,
    title: impactNamesByLanguage.en[ImpactCategoryId.DISTRIBUTING_KNOWLEDGE],
    dimension: Dimension.KNOWLEDGE,
    explanations: {
      impactCategoryCode: ImpactCategoryId.DISTRIBUTING_KNOWLEDGE,
      topicsConsidered: [
        'Distribution of knowledge via traditional and digital channels',
        'Transmission of data',
        'Knowledge distribution through education',
        'Distribution of misinformation and spam content',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Distributing untrue or misleading information, or spreading spam content that takes up space from trustworthy information and burdens human cognitive capacity. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example: a company runs a social media platform that allows sharing of fake news articles.`,
        positiveExplanation: `Distributing already existing data, information or knowledge. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company broadcasts television programs.',
      },
      productViewExplanation: 'Distributing knowledge.',
    },
  },
  [ImpactCategoryId.SCARCE_HUMAN_CAPITAL]: {
    code: ImpactCategoryId.SCARCE_HUMAN_CAPITAL,
    title: impactNamesByLanguage.en[ImpactCategoryId.SCARCE_HUMAN_CAPITAL],
    dimension: Dimension.KNOWLEDGE,
    explanations: {
      impactCategoryCode: ImpactCategoryId.SCARCE_HUMAN_CAPITAL,
      topicsConsidered: [
        'The opportunity cost of employing people with scarce skills and capabilities. Scarce human capital is regarded as a resource utilized by a company, similar to environmental resources. If a company reserves or utilized scarce human capital, it needs to put these resources into use and achieve something on the positive side in order to be considered net positive.',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `The opportunity cost of employing people with scarce skills and capabilities. This impact reflects the fact that oftentimes, the workforce is a company's most valuable resource. If a company reserves or utilized scarce human capital, it needs to put these resources into use and achieve something on the positive side in order to be considered net positive. In this impact, we take into account people employed both directly by the company, as well as indirectly by its suppliers and customers.`,
        negativeExample: `Example: a company employs 140 highly skilled programmers.`,
      },
      productViewExplanation: `Employing people with high opportunity cost, such as
         a high level of education or human capital that is scarce in society.
          This human capital is seen as a resource a company uses, in the same manner as environmental resources.`,
    },
  },
  [ImpactCategoryId.PHYSICAL_DISEASES]: {
    code: ImpactCategoryId.PHYSICAL_DISEASES,
    title: impactNamesByLanguage.en[ImpactCategoryId.PHYSICAL_DISEASES],
    dimension: Dimension.HEALTH,
    explanations: {
      impactCategoryCode: ImpactCategoryId.PHYSICAL_DISEASES,
      topicsConsidered: ['Chronic diseases', 'Infectious diseases', 'Nutritional deficiencies', 'Injuries'],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Causing or contributing towards the development or occurrence of physical diseases, injuries or fatalities. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: 'Example: a company produces cigarettes which have been proven to cause lung cancer.',
        positiveExplanation: `Treating, preventing or contributing towards the treatment or prevention of physical diseases, injuries or fatalities. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company develops and produces vaccines.',
      },
      productViewExplanation: 'Preventing, treating or causing physical diseases or injuries among humans.',
    },
  },
  [ImpactCategoryId.MENTAL_DISEASES]: {
    code: ImpactCategoryId.MENTAL_DISEASES,
    title: impactNamesByLanguage.en[ImpactCategoryId.MENTAL_DISEASES],
    dimension: Dimension.HEALTH,
    explanations: {
      impactCategoryCode: ImpactCategoryId.MENTAL_DISEASES,
      topicsConsidered: ['Mental diseases', 'Addictions', 'Sleep'],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Causing or contributing towards the development of mental health problems. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example: a company produces slot machines that cause addiction.`,
        positiveExplanation: `Treating, preventing or contributing towards the treatment and prevention of mental health problems. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: `Example: a company offers psychotherapy services for the treatment of depression.`,
      },
      productViewExplanation: 'Preventing, treating or causing mental health issues among humans.',
    },
  },
  [ImpactCategoryId.NUTRITION]: {
    code: ImpactCategoryId.NUTRITION,
    title: impactNamesByLanguage.en[ImpactCategoryId.NUTRITION],
    dimension: Dimension.HEALTH,
    explanations: {
      impactCategoryCode: ImpactCategoryId.NUTRITION,
      topicsConsidered: [
        'Nutritional quality of food',
        'Availability of food',
        'Access to food',
        'Food stability',
        'Enabling utilization of food',
      ],
      explanationsAndExamplesByValence: {
        positiveExplanation: `Encouraging, enabling or providing healthy nutrition or contributing to food security. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: `Example: a company sells legumes, which have been proven to be a healthy source of protein and various other nutrients. `,
      },
      productViewExplanation: 'Encouraging or enabling healthy diets or providing healthy nutrition for humans.',
    },
  },
  [ImpactCategoryId.RELATIONSHIPS]: {
    code: ImpactCategoryId.RELATIONSHIPS,
    title: impactNamesByLanguage.en[ImpactCategoryId.RELATIONSHIPS],
    dimension: Dimension.HEALTH,
    explanations: {
      impactCategoryCode: ImpactCategoryId.RELATIONSHIPS,
      topicsConsidered: [
        'Social interactions',
        'Violence and aggression',
        'Communities',
        'Emotional and social intelligence',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Worsening the quality of human relationships and connection. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example: a company produces alcohol that causes aggression and violence.`,
        positiveExplanation: `Improving the quality of human relationships and connection. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: `Example: a company offers couple therapy services that help individuals establish and maintain healthy relationships.`,
      },
      productViewExplanation: 'Improving or worsening quality of human relationships and connection.',
    },
  },
  [ImpactCategoryId.MEANING_AND_JOY]: {
    code: ImpactCategoryId.MEANING_AND_JOY,
    title: impactNamesByLanguage.en[ImpactCategoryId.MEANING_AND_JOY],
    dimension: Dimension.HEALTH,
    explanations: {
      impactCategoryCode: ImpactCategoryId.MEANING_AND_JOY,
      topicsConsidered: [
        'Eudaimonic well-being (meaning, life satisfaction, purpose)',
        'Hedonistic well-being (joy, pleasure, enjoyment)',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Decreasing experiences of joy and sense of meaning. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example: a company produces fashion advertisements that enforce beauty standards causing anxiety and loss of self-esteem.`,
        positiveExplanation: `Increasing experiences of joy and sense of meaning. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company produces chocolate, which makes some people feel enjoyment.',
      },
      productViewExplanation: 'Increasing or decreasing experiences of sense of meaning and joy among humans.',
    },
  },
  [ImpactCategoryId.GHG_EMISSIONS]: {
    code: ImpactCategoryId.GHG_EMISSIONS,
    title: impactNamesByLanguage.en[ImpactCategoryId.GHG_EMISSIONS],
    dimension: Dimension.ENVIRONMENT,
    explanations: {
      impactCategoryCode: ImpactCategoryId.GHG_EMISSIONS,
      topicsConsidered: [
        'Carbon dioxide (CO₂)',
        'Nitrous oxide (N₂O)',
        'Methane (CH₄)',
        'Ozone (O₃)',
        'Chlorofluorocarbons (CFCs)',
        'Hydrofluorocarbons (HFCs)',
        'Perfluorocarbons (PFCs)',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Creating greenhouse gas emissions. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample:
          'Example: a company produces dairy products, which generates carbon dioxide and methane emissions.',
        positiveExplanation: `Removing or contributing towards the reduction of greenhouse gas emissions, or producing or enabling products and services that create fewer GHG emissions compared to their most common alternatives. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company creates carbon-capture technology, or produces wind power.',
      },
      productViewExplanation:
        'Creating or removing emissions that fall under the definition of greenhouse gas emissions.',
    },
  },
  [ImpactCategoryId.NON_GHG_EMISSIONS]: {
    code: ImpactCategoryId.NON_GHG_EMISSIONS,
    title: impactNamesByLanguage.en[ImpactCategoryId.NON_GHG_EMISSIONS],
    dimension: Dimension.ENVIRONMENT,
    explanations: {
      impactCategoryCode: ImpactCategoryId.NON_GHG_EMISSIONS,
      topicsConsidered: [
        'Particulate matter',
        'Heavy metal emissions',
        'Volatile organic compounds (VOCs)',
        'Sulfur dioxide (SO₂)',
        'Ammonia (NH3)',
        'Nitrogen oxides (NOx)',
        'Persistent organic pollutants (POPs)',
        'Persistent, bioaccumulative and toxic substances (PBTs)',
        'Microplastics',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Creating non-GHG emissions, such as land, water and air pollution. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example: a company produces fertilizers that contain ammonia which can seep into lakes, or diesel-powered passenger cars that create particulate emissions.`,
        positiveExplanation: `Removing or contributing towards the reduction of non-GHG emissions, such as land, water and air pollution, or producing or enabling products and services that create less non-GHG emissions compared to their most common alternatives. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: `Example: a company produces oil spill skimmers, which are used to clean up oil from contaminated water bodies.`,
      },
      productViewExplanation: `Creating or removing other than greenhouse
      gas emissions, such as land, sea or air pollution.`,
    },
  },
  [ImpactCategoryId.SCARCE_NATURAL_RESOURCES]: {
    code: ImpactCategoryId.SCARCE_NATURAL_RESOURCES,
    title: impactNamesByLanguage.en[ImpactCategoryId.SCARCE_NATURAL_RESOURCES],
    dimension: Dimension.ENVIRONMENT,
    explanations: {
      impactCategoryCode: ImpactCategoryId.SCARCE_NATURAL_RESOURCES,
      topicsConsidered: [
        'Critical raw materials: raw materials that are economically and strategically important for the economy, but have a high-risk associated with their supply',
        'Fresh water consumption and pollution',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `The use of highly scarce natural resources, such as fresh water, or scarce minerals and metals. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: `Example:  a company runs an industrial process that uses large amounts of fresh water, or produces solar panels with rare earth metal components.`,
        positiveExplanation: `Saving or increasing the amount of highly scarce natural resources, such as fresh water, and scarce minerals and metals. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: `Example: a company produces water desalination systems, which increase the amount of fresh drinking water available.`,
      },
      productViewExplanation: `Using fresh water, minerals or metals in scarce areas and contexts.`,
    },
  },
  [ImpactCategoryId.BIODIVERSITY]: {
    code: ImpactCategoryId.BIODIVERSITY,
    title: impactNamesByLanguage.en[ImpactCategoryId.BIODIVERSITY],
    dimension: Dimension.ENVIRONMENT,
    explanations: {
      impactCategoryCode: ImpactCategoryId.BIODIVERSITY,
      topicsConsidered: [
        'Habitat loss',
        'Endangered species',
        'Invasive alien species',
        'Animal suffering',
        'Land use',
        'Land degradation',
        'Desertification',
        'Soil sealing',
        'Ecosystem services',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Destroying biodiversity or harming animal welfare. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample:
          'Example:  a company cuts down forests to produce rapeseed oil, or utilises intensive animal farming to produce meat products.',
        positiveExplanation: `Protecting or increasing biodiversity or animal welfare. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample: 'Example: a company breeds bees which help pollinate surrounding flora.',
      },
      productViewExplanation: 'Destroying, protecting or increasing biodiversity and animal welfare.',
    },
  },
  [ImpactCategoryId.WASTE]: {
    code: ImpactCategoryId.WASTE,
    title: impactNamesByLanguage.en[ImpactCategoryId.WASTE],
    dimension: Dimension.ENVIRONMENT,
    explanations: {
      impactCategoryCode: ImpactCategoryId.WASTE,
      topicsConsidered: [
        'All types of solid and liquid materials that have been discarded as worthless, defective, or of no use',
        'Both non-hazardous and hazardous waste',
      ],
      explanationsAndExamplesByValence: {
        negativeExplanation: `Creating all types of waste. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        negativeExample: 'Example: a company manufactures disposable plastic cups.',
        positiveExplanation: `Treating waste and encouraging, enabling or practicing recycling or the re-use of materials. This can occur either directly through the company's core products and services, indirectly through its suppliers' operations or when its products and services are used by customers.`,
        positiveExample:
          'Example: a company treats hazardous waste, or runs a platform on which customers can sell used goods.',
      },
      productViewExplanation:
        'Producing waste or encouraging, enabling or practicing recycling or re-use of materials.',
    },
  },
};
